import { useState } from "react";

import { IAdminPatientDetail } from "../../../../@types/admin/AdminPatient";
import {
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  Tab,
  TabsContainer,
} from "../AdminPage.styles";
import { AuditLogPanel } from "../AuditLogPanel";
import { PanelStack } from "./PatientDetailsPanel.styles";

/** Audited tables behind the three tabs (see the AuditLogPanel docs). */
const PATIENT_TABLE = "cms_patient";
const USER_TABLE = "cms_user";
const CONDITION_TABLE = "cms_condition";

type IHistoryTab = "patient" | "user" | "condition";

interface IPatientHistoryTabProps {
  patient: IAdminPatientDetail;
}

/**
 * History tab of the patient detail panel (EN-5169).
 *
 * Retool had three separate "history" links — on the patient form, the user
 * form and the conditions container — each opening `moduleAuditLogs` in its own
 * modal. The portal has one global modal slot and the panel's own row
 * drill-down uses it, so they are tabs here (the EN-5147 rule).
 *
 * The patient and user trails read `/Retool/AuditLogs`, whose permission is
 * `audit-logs:read` (`AuditLogsAccessHandler`), not a page permission — so
 * holding `patients:access` alone is not enough for them. The condition trail
 * goes through `GET /Retool/Patients/{id}/GetConditionAuditLogs` on the
 * ordinary Retool policy.
 */
export function PatientHistoryTab({ patient }: IPatientHistoryTabProps) {
  const [tab, setTab] = useState<IHistoryTab>("patient");

  return (
    <PanelStack>
      <SectionCard>
        <SectionHeader>
          <SectionHeading>
            <SectionTitle>History</SectionTitle>
          </SectionHeading>
        </SectionHeader>

        <TabsContainer>
          <Tab
            type="button"
            active={tab === "patient"}
            onClick={() => setTab("patient")}
          >
            Patient record
          </Tab>
          <Tab
            type="button"
            active={tab === "user"}
            onClick={() => setTab("user")}
          >
            User account
          </Tab>
          <Tab
            type="button"
            active={tab === "condition"}
            onClick={() => setTab("condition")}
          >
            Conditions
          </Tab>
        </TabsContainer>

        {tab === "patient" && (
          <AuditLogPanel
            key="patient-history"
            tableName={PATIENT_TABLE}
            entityId={Number(patient.id)}
            title="Patient record changes"
          />
        )}
        {tab === "user" && (
          <AuditLogPanel
            key="user-history"
            tableName={USER_TABLE}
            entityId={patient.userId}
            title="User account changes"
          />
        )}
        {tab === "condition" && (
          <AuditLogPanel
            key="condition-history"
            tableName={CONDITION_TABLE}
            // The condition trail is keyed on the PATIENT id, not a condition
            // id — its endpoint is per-patient (see the AuditLogPanel docs).
            entityId={Number(patient.id)}
            title="Condition changes"
          />
        )}
      </SectionCard>
    </PanelStack>
  );
}
